/**
 * Policies list (real `/v2/policies/` backend) — OR-1664, backend OR-1604.
 *
 *   GET   /v2/policies/            paged, filtered, sorted, searched list
 *   PATCH /v2/policies/{id}        { priority } — set or clear the priority
 *
 * One row per policy, read from its **current term**: the in-force term,
 * else the most recent by effective date. The backend computes
 * `total_premium` (written premium: issued entries only, taxes and fees
 * excluded) and `facility_count` (open coverage rows); nothing here is
 * recalculated. A policy bound since the lifecycle rollout reads $0 until
 * its issuance is issued (milestone 3) — that is the backend's figure, not
 * a display bug.
 *
 * `search` matches the policy number (full and root), the named insured
 * and the brokerage name. `sort` is a column name, `-` prefix for
 * descending, from the `PolicyListSort` set below.
 *
 * Not on this surface: the legacy per-submission `/v2/policy` routes
 * (`./policy.ts`) still back Policy Details and the document buttons.
 */
import { getJsonReal, sendJsonReal } from "../client";

export const POLICY_PRIORITIES = ["high", "medium", "low"] as const;
export type PolicyPriority = (typeof POLICY_PRIORITIES)[number];

/** Status of the current term. Mirrors `PolicyTermStatus` in
 *  `src/models/policy.py` (orion-backend). */
export const POLICY_TERM_STATUSES = [
    "in_force",
    "pending_cancellation",
    "bound",
    // Issued before its effective date; turns In force when it starts (backend #431).
    "pending_inception",
    "run_off",
    "cancelled",
    "expired",
    "void",
] as const;
export type PolicyTermStatus = (typeof POLICY_TERM_STATUSES)[number];

/** Sortable columns, as the backend names them. */
export const POLICY_SORT_FIELDS = [
    "policy_number",
    "named_insured",
    "total_premium",
    "effective_date",
    "expiration_date",
    "facility_count",
    "brokerage_name",
] as const;
export type PolicySortField = (typeof POLICY_SORT_FIELDS)[number];

export interface PolicyListItemWire {
    id: string;
    /** The current term: in force, else the most recent. */
    policy_term_id: string;
    /** Full number, term suffix included (`S0079PK000146-00`). */
    policy_number: string;
    priority: PolicyPriority | null;
    /** First named insured. */
    named_insured: string;
    status: PolicyTermStatus;
    /** ISO `YYYY-MM-DD`. Start of the current term — "Inception Date" on screen. */
    effective_date: string;
    /** ISO `YYYY-MM-DD`. End of the current term. */
    expiration_date: string;
    /** Decimal string, two places. Written premium, taxes and fees excluded. */
    total_premium: string;
    facility_count: number;
    brokerage_name: string | null;
}

export interface PolicyListResponse {
    /** Policies matching the filters, ignoring paging. */
    total: number;
    items: PolicyListItemWire[];
}

export interface ListPoliciesParams {
    offset?: number;
    limit?: number;
    search?: string;
    /** `field` ascending, `-field` descending. Backend default: `-effective_date`. */
    sort?: string;
    priority?: PolicyPriority[];
    status?: PolicyTermStatus[];
    brokerageId?: string;
    /** ISO `YYYY-MM-DD`, inclusive. */
    minEffectiveDate?: string;
    maxEffectiveDate?: string;
    minExpirationDate?: string;
    maxExpirationDate?: string;
}

export function listPolicies(params: ListPoliciesParams = {}): Promise<PolicyListResponse> {
    const qs = new URLSearchParams();
    if (params.offset !== undefined) qs.set("offset", String(params.offset));
    if (params.limit !== undefined) qs.set("limit", String(params.limit));
    if (params.search) qs.set("search", params.search);
    if (params.sort) qs.set("sort", params.sort);
    // Repeated params: FastAPI reads `list[...]` query params as `?status=a&status=b`.
    for (const p of params.priority ?? []) qs.append("priority", p);
    for (const s of params.status ?? []) qs.append("status", s);
    if (params.brokerageId) qs.set("brokerage_id", params.brokerageId);
    if (params.minEffectiveDate) qs.set("min_effective_date", params.minEffectiveDate);
    if (params.maxEffectiveDate) qs.set("max_effective_date", params.maxEffectiveDate);
    if (params.minExpirationDate) qs.set("min_expiration_date", params.minExpirationDate);
    if (params.maxExpirationDate) qs.set("max_expiration_date", params.maxExpirationDate);
    const query = qs.toString();
    return getJsonReal<PolicyListResponse>(`/v2/policies/${query ? `?${query}` : ""}`);
}

/** Set or clear a policy's priority. Returns the row as it now reads on
 *  the list. Priority sits on the policy (the lineage root), so it carries
 *  over at renewal — provisional per backend OR-1604. */
export function updatePolicyPriority(policyId: string, priority: PolicyPriority | null): Promise<PolicyListItemWire> {
    return sendJsonReal<PolicyListItemWire>(`/v2/policies/${encodeURIComponent(policyId)}`, "PATCH", { priority });
}
